import { defineComponent as _defineComponent } from 'vue'
import BButton from 'JS/components/BButton.vue';
import bugsnagClient from 'JS/global/bugsnag';
import CarouselTrackingService from 'JS/services/CarouselTrackingService';
import globalData from 'Src/js/global/window-data';
import Icon from 'JS/components/Icon.vue';
import Price from 'JS/catalog/components/product-list/price/ProductListPrice.vue';
import ProductService, { AddToCartResult, IPDPAddToCartResponse } from 'JS/services/ProductService';
import { computed, ref } from 'vue';
import { createCarouselClickData, createCarouselClickGtmData } from 'JS/utilities/carousel-tracking';
import { defaultFulfillmentMethod } from 'JS/utilities/product/default-fulfillment-method';
import { FulfillmentType } from 'JS/types/ProductDelivery';
import { IProductCarouselProduct, MinimalProductCarousel } from 'JS/types/Carousel';
import { IQuickCartSummaryJson } from 'JS/types/Order';


export default /*#__PURE__*/_defineComponent({
  __name: 'QuickAddProductDetails',
  props: {
    carousel: null,
    product: null,
    fulfillmentType: null,
    loading: { type: Boolean },
    viewImpressionUrl: null,
    hideReviewStars: { type: Boolean },
    customCarouselID: null
  },
  emits: ["loading", "maxhit"],
  setup(__props: any, { emit }: { emit: ({
	(e: 'loading', loading: boolean): void,
	(e: 'maxhit'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	carousel: MinimalProductCarousel,
	product: IProductCarouselProduct,
	fulfillmentType?: FulfillmentType,
	loading: boolean,
	viewImpressionUrl?: string,
	hideReviewStars?: boolean,
	customCarouselID?: string,
}



const productService = new ProductService();
const productAdded = ref<boolean>(false);
const productAddError = ref<boolean>(false);
const addToCartText = computed<string>(() => productAddError.value ? 'Try Again' : 'Add To Cart');
const _trackingSvc = new CarouselTrackingService();



const price = computed(() => {
	if (props.product.price.pricePromoText === "Today's Price") {
		return {
			...props.product.price,
			pricePromoText: '',
		};
	}

	return props.product.price;
});

function navigateToPdp(): void {
	window.location.href = props.product.productUrl;
}

const canAddToCart = computed(() => !(
	props.product.isGroupedProduct ||
	props.product.isRestrictedProduct ||
	props.product.isNotBuyableOnline ||
	props.product.isSoldOut
));

async function addToCart(): Promise<void> {
	emit('loading', true);

	//Default viewImpressionUrl similar to buyItAgainCarousel
	const viewImpressionUrl = props.viewImpressionUrl || (window.location.pathname + window.location.search);

	const clickData = createCarouselClickData(
		props.product,
		props.carousel,
		null,
		viewImpressionUrl
	);

	const gtmData = createCarouselClickGtmData(props.product, props.carousel, props.customCarouselID);

	_trackingSvc.trackClick(clickData, gtmData); //Track 'carousel' clickthrough

	const fulfillmentType: FulfillmentType =
		props.fulfillmentType
		|| defaultFulfillmentMethod({
			hasExistingCart: (globalData?.cartSummary?.cartQty ?? 0) > 0,
			canShip: props.product.canShip,
			canPickup: props.product.canPickup,
			canDeliver: props.product.canDeliver,
			hasItemsToShip: (globalData?.cartSummary?.hasItemsToShip ?? false),
			hasPickupItems: (globalData?.cartSummary?.hasPickupItems ?? false),
			hasDeliveryItems: (globalData?.cartSummary?.hasDeliveryItems ?? false),
			bopusOnly: false });

	const response: IPDPAddToCartResponse = await productService.addToCart({
		blainNumber: props.product.blainNumber,
		qty: 1,
		fulfillmentType: fulfillmentType,
		userConfirmed: true,
		userAgreed: false, // quick add products should not have a product agreement in the first place
		updateSubscriptionRequest: null,
		skipUpsell: true,
		ignoreFulfillmentType: true
	});

	addToCartResponse(response, fulfillmentType);
}


function addToCartResponse(res: IPDPAddToCartResponse, fulfillmentType: FulfillmentType): void {
	if (res == null) {
		productAddError.value = true;
		emit('loading', false);
		return;
	}

	if (res.result == AddToCartResult.MaxLines) {
		emit('maxhit');
		productAddError.value = false;
		emit('loading', false);
		return;
	} else if (res.result != AddToCartResult.Success) {
		if (res.result != AddToCartResult.CannotAdd) {
			//shouldn't be able to reach this, fulfillment type errors shouldn't occur here
			bugsnagClient.notify(new Error('Error type should be unreachable when adding upsell product to cart from pdp.'),  {
				metaData: res
			});
		}
		productAddError.value = true;
	}

	// if response is successful show added to cart modal by setting addingToCart to false
	if (res.result == AddToCartResult.Success) {
		// if cartSummary, update header with new cart data
		updateHeader(res.cartSummary);
		productAdded.value = true;
		productAddError.value = false;
		const cartProduct = res.cartSummary.productsInCart.find(cartProduct => cartProduct.blainNumber === props.product.blainNumber);
		// Add to Cart event for Google Tag Manager
		const orderProducts = res.cartSummary.productsInCart.map(item => ({
			blainNumber: item.price.blainNumber,
			qty: item.qty,
			unitPrice: item.price.price,
			vendorBrand: item.vendorBrand,
			productName: item.productName,
			imageUrl: item.imageUrlLarger,
			productUrl: item.virtualURL
		}));

		window.blainGtmDataLayer.push({ orderProducts: null }); // orderProducts needs to be reset first to avoid conflicts
		window.blainGtmDataLayer.push({
			'blainNumber': props.product.blainNumber,
			'vendorBrand': props.product.vendorBrand,
			'productName': props.product.productName,
			'deliveryChannel': fulfillmentType,
			'totalValue': cartProduct.unitPrice,
			'qty': 1,
			'orderProducts': orderProducts,
			'orderId': res.orderID,
			'event': "cart_ajax_add"
		});
	}

	emit('loading', false);
}

function updateHeader(cartSummary: IQuickCartSummaryJson): void {
	if (window.vueEventBus && cartSummary) {
		window.vueEventBus.$emit('update-quick-cart', cartSummary);
	}
}


return { __sfc: true,props, productService, productAdded, productAddError, addToCartText, _trackingSvc, emit, price, navigateToPdp, canAddToCart, addToCart, addToCartResponse, updateHeader, BButton, Icon, Price }
}

})